/** Adapts BackendClient (any transport) to the session layer's LockstepLink. */
import type { LockstepEvent, LockstepLink } from '../../session/lockstep';
import type { BackendClient } from './client';

export function createLockstepLink(client: BackendClient, sessionId: string): LockstepLink {
  return {
    async sendInput(batch) {
      const r = await client.call('session.input', { sessionId, turn: batch.turn, fromTick: batch.fromTick, throughTick: batch.throughTick, commands: batch.commands });
      return { throughTick: r.throughTick };
    },
    async sendEndTurn(req) {
      await client.call('session.endTurn', {
        sessionId,
        turn: req.turn,
        tick: req.tick,
        nextTeam: req.nextTeam,
        ...(req.forcedEnd !== undefined ? { forcedEnd: req.forcedEnd } : {}),
      });
    },
    async sendHash(req) {
      await client.call('session.hash', { sessionId, tick: req.tick, hash: req.hash, algorithm: req.algorithm });
    },
    async fetchLog() {
      return (await client.call('session.get', { sessionId })).log;
    },
    subscribe(listener: (e: LockstepEvent) => void) {
      const offs = [
        client.on('session.input', (e) => {
          if (e.sessionId === sessionId) listener({ type: 'input', team: e.team, batch: { turn: e.turn, fromTick: e.fromTick, throughTick: e.throughTick, commands: e.commands } });
        }),
        client.on('session.turn', (e) => {
          if (e.sessionId !== sessionId) return;
          if (e.team === null || e.startTick === null) {
            // A forced turn: the server closed its input after endTick; the next team is reported by a participant.
            listener({ type: 'inputClosed', closed: { turn: e.endedTurn, tick: e.endTick, deadlineAt: e.deadlineAt ?? undefined } });
            return;
          }
          listener({
            type: 'turn',
            change: { endedTurn: e.endedTurn, endTick: e.endTick, turn: e.turn, team: e.team, forced: e.forced, deadlineAt: e.deadlineAt ?? undefined },
          });
        }),
        client.on('session.desync', (e) => {
          if (e.sessionId === sessionId) listener({ type: 'desync', desync: { tick: e.tick, hashes: e.hashes } });
        }),
        client.on('session.ended', (e) => {
          if (e.sessionId === sessionId) {
            listener({ type: 'ended', winnerTeam: e.outcome.result.winnerTeam, verified: e.outcome.verified, reason: e.outcome.reason ?? undefined });
          }
        }),
      ];
      return () => offs.forEach((off) => off());
    },
  };
}
